"use client";

import { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import type { Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Save, Loader2, Tag } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { ProductFormValues, schema } from "./schema/schema";
import AddonsStep from "./components/AddonStep";
import BasicInfoStep from "./components/BasicInfoStep";
import { ProductsService } from "@/services/ProductsService";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { FileService } from "@/services/FileService";
import { FilePurpose } from "@/interfaces/files";
import {
  ProductsInput,
  AdditionalItemType,
  ProductAdditionalGroupResponse,
  ProductAdditionalItemResponse,
} from "@/interfaces/products";
import { ProductAdditionalsService } from "@/services/ProductAdditionalsService";

export default function ProductForm({
  isEditing: isEditingProp = false,
}: {
  isEditing?: boolean;
}) {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [isEditing, setIsEditing] = useState<boolean>(isEditingProp);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [initialImage, setInitialImage] = useState<string | undefined>(
    undefined
  );
  const [existingFileId, setExistingFileId] = useState<string | undefined>();

  const filesService = new FileService();

  // Função para converter ProductFormValues para ProductsInput
  const convertToProductsInput = (
    data: ProductFormValues,
    fileId?: string
  ): ProductsInput => {
    return {
      name: data.name,
      description: data.description || "",
      unitPrice: data.unitPrice,
      unitCost: data.unitCost,
      fileId: fileId ?? null,
      tags: data.tags || [],
    };
  };

  const saveAdditionals = async (
    productId: string,
    additionals: ProductFormValues["additional"]
  ) => {
    const additionalsService = new ProductAdditionalsService(productId);

    let existingGroups: ProductAdditionalGroupResponse[] = [];
    try {
      const existing = await additionalsService.findAll();
      existingGroups = existing.data ?? [];
    } catch {
      existingGroups = [];
    }

    const existingGroupsMap = new Map(
      existingGroups
        .filter((group): group is ProductAdditionalGroupResponse & { id: string } =>
          Boolean(group.id)
        )
        .map((group) => [group.id, group])
    );

    const handledGroupIds = new Set<string>();

    for (const [groupIndex, group] of additionals.entries()) {
      const groupPayload = {
        name: group.name,
        selectionType: group.selectionType,
        minQuantity: group.minQuantity,
        maxQuantity: group.maxQuantity,
        position: groupIndex + 1,
      };

      let groupId = group.id;

      if (groupId && existingGroupsMap.has(groupId)) {
        await additionalsService.update(groupId, groupPayload);
      } else {
        const createdGroup = await additionalsService.create(groupPayload);
        groupId = createdGroup.id;
      }

      if (!groupId) {
        continue;
      }

      handledGroupIds.add(groupId);

      const existingGroup = group.id ? existingGroupsMap.get(group.id) : undefined;
      const existingItemsMap = new Map<string, ProductAdditionalItemResponse>(
        (existingGroup?.items ?? [])
          .filter(
            (item): item is ProductAdditionalItemResponse & { id: string } =>
              Boolean(item.id)
          )
          .map((item) => [item.id, item])
      );

      const handledItemIds = new Set<string>();

      for (const [itemIndex, item] of group.items.entries()) {
        let fileId = item.fileId ?? null;

        if (item.file) {
          const uploadedFile = await filesService.upload({
            file: item.file,
            purpose: FilePurpose.PRODUCTS,
          });
          fileId = uploadedFile.id;
        }

        const itemPayload = {
          label: item.name,
          unitPrice: Number(item.unitPrice ?? 0),
          unitCost: Number(item.unitCost ?? 0),
          consumptionFactor: Number(item.consumptionFactor ?? 1),
          position: itemIndex + 1,
          productId: item.productId || null,
          fileId: fileId ?? null,
        };

        if (item.id && existingItemsMap.has(item.id)) {
          await additionalsService.updateItem(groupId, item.id, itemPayload);
          handledItemIds.add(item.id);
        } else {
          const createdItem = await additionalsService.createItem(groupId, itemPayload);
          if (createdItem.id) {
            handledItemIds.add(createdItem.id);
          }
        }
      }

      for (const existingItemId of existingItemsMap.keys()) {
        if (!handledItemIds.has(existingItemId)) {
          await additionalsService.deleteItem(groupId, existingItemId);
        }
      }
    }

    for (const existingGroup of existingGroups) {
      if (existingGroup.id && !handledGroupIds.has(existingGroup.id)) {
        await additionalsService.delete(existingGroup.id);
      }
    }
  };

  const form = useForm<ProductFormValues, undefined, ProductFormValues>({
    resolver: zodResolver(schema) as Resolver<ProductFormValues, undefined, ProductFormValues>,
    defaultValues: {
      name: "",
      description: "",
      unitPrice: 0,
      unitCost: 0,
      tags: [],
      additional: [],
    },
  });

  useEffect(() => {
    const service = new ProductsService();

    const fetchInitialData = async () => {
      try {
        setIsLoading(true);

        if (id) {
          setIsEditing(true);
          const fetchedProduct = await service.findById(id, {
            expand: ["file", "tags"],
          });

          const additionalsService = new ProductAdditionalsService(id);
          const fetchedAdditionals = await additionalsService.findAll();

          const sortedAdditionals = [...(fetchedAdditionals.data ?? [])]
            .sort((a, b) => (a.position || 0) - (b.position || 0))
            .map((group) => ({
              ...group,
              items:
                group.items
                  ?.slice()
                  .sort((a, b) => (a.position || 0) - (b.position || 0)) || [],
            }));

          // Verificar se as tags são objetos ou strings
          let tagIds: string[] = [];
          if (Array.isArray(fetchedProduct.tags)) {
            tagIds = fetchedProduct.tags.map((tag) =>
              typeof tag === "string" ? tag : tag.id
            );
          }

          // Garantir que os valores numéricos sejam números
          const unitPrice =
            typeof fetchedProduct.unitPrice === "number"
              ? fetchedProduct.unitPrice
              : 0;
          const unitCost =
            typeof fetchedProduct.unitCost === "number"
              ? fetchedProduct.unitCost
              : 0;

          // Usar setTimeout para garantir que o form.reset seja executado após a renderização
          setTimeout(() => {
            form.reset({
              name: fetchedProduct.name || "",
              description: fetchedProduct.description || "",
              unitPrice: unitPrice,
              unitCost: unitCost,
              tags: tagIds,
              additional: sortedAdditionals.map((group) => ({
                id: group.id,
                name: group.name,
                selectionType: group.selectionType,
                minQuantity: group.minQuantity,
                maxQuantity: group.maxQuantity,
                items: group.items.map((item) => ({
                  id: item.id,
                  name: item.label || "",
                  unitPrice: item.unitPrice,
                  unitCost: item.unitCost || 0,
                  productId: item.productId || null,
                  fileId: item.fileId || undefined,
                  file: undefined,
                  type: item.productId
                    ? AdditionalItemType.PRODUCT
                    : AdditionalItemType.CUSTOM,
                  consumptionFactor: item.consumptionFactor ?? 1,
                })),
              })),
            });

            // Caso o produto já possua uma imagem, define a imagem inicial
            if (fetchedProduct.file?.url) {
              setInitialImage(fetchedProduct.file.url);
            }
            if (fetchedProduct.fileId || fetchedProduct.file?.id) {
              setExistingFileId(
                fetchedProduct.fileId || fetchedProduct.file?.id
              );
            }

            setIsLoading(false);
          }, 100);
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Erro ao carregar dados iniciais:", error);
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [id, form]);

  const onSubmit = async (data: ProductFormValues) => {
    const service = new ProductsService();

    setIsSubmitting(true);
    try {
      let fileId = existingFileId;

      // Se houver um arquivo selecionado, faça o upload dele
      if (selectedFile) {
        const fileInput = {
          file: selectedFile,
          purpose: FilePurpose.PRODUCTS,
        };
        const uploadedFile = await filesService.upload(fileInput);
        fileId = uploadedFile.id;
      }

      // Converter para o formato esperado pelo serviço
      const productsInput = convertToProductsInput(data, fileId);

      let savedProduct;
      if (isEditing && id) {
        savedProduct = await service.update(id, productsInput);
      } else {
        savedProduct = await service.create(productsInput);
      }

      const productId = savedProduct.id || id;
      if (productId) {
        await saveAdditionals(productId, data.additional || []);
      }

      navigate("/produtos");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 mx-auto max-w-4xl">
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit, (error) =>
            console.error(error)
          )}
        >
          <Card className="overflow-hidden border-none shadow-lg rounded-2xl">
            <CardHeader className="bg-[#EE7B11] text-white p-4">
              <div className="flex items-center gap-3">
                <Tag className="h-6 w-6" />
                <CardTitle className="text-xl font-semibold">
                  {isEditing ? "Editar Produto" : "Novo Produto"}
                </CardTitle>
              </div>
            </CardHeader>

            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="sticky top-0 z-10 grid w-full grid-cols-2 bg-gray-100 h-auto rounded-none">
                <TabsTrigger
                  value="basic"
                  className="py-3 data-[state=active]:bg-white/90 data-[state=active]:shadow-sm data-[state=active]:border-b-2 data-[state=active]:border-[#EE7B11]"
                >
                  Dados Básicos
                </TabsTrigger>
                <TabsTrigger
                  value="adicionais"
                  className="py-3 data-[state=active]:bg-white/90 data-[state=active]:shadow-sm data-[state=active]:border-b-2 data-[state=active]:border-[#EE7B11]"
                >
                  Adicionais
                </TabsTrigger>
              </TabsList>

              <TabsContent value="basic">
                <BasicInfoStep
                  initialImage={initialImage}
                  onImageChange={setSelectedFile}
                />
              </TabsContent>
              <TabsContent value="adicionais">
                <AddonsStep />
              </TabsContent>
            </Tabs>

            <CardFooter className="border-t p-4 sm:p-6 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/produtos")}
                className="border-gray-300 text-gray-700 hover:bg-gray-50 bg-transparent"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
              >
                {isSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                {isEditing ? "Atualizar" : "Salvar"}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </FormProvider>
    </div>
  );
}







